import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Layers, Users, CheckCircle } from 'lucide-react';
import { optimizeImage, imageUrls } from '../utils/imageOptimizer';
import SEO from '../components/SEO';

const benefits = [
  {
    icon: Palette,
    title: 'Jeres design, korrekt realiseret',
    description: 'I udvikler idé og design – vi sikrer, at det kan produceres og fungere i praksis. Vi omsætter jeres vision til færdig skiltning og wayfinding uden at gå på kompromis med udtryk eller kvalitet.',
  },
  {
    icon: Layers,
    title: 'Komplekse projekter, én leverance',
    description: 'Vi håndterer produktion, logistik og montering på tværs af løsninger. Én samlet leverance, så I undgår koordinering på tværs af flere leverandører.',
  },
  {
    icon: Users,
    title: 'Direkte samarbejde',
    description: 'I får én fast kontaktperson, der forstår jeres proces og taler jeres sprog. Effektiv sparring – uden mellemled.',
  },
  {
    icon: CheckCircle,
    title: 'Dokumenteret leverance',
    description: 'Vi har +30 års erfaring med at levere til arkitekter og designbureauer. Løsningerne er gennemtestede og leveres som aftalt – hver gang.',
  },
];

const capabilities = [
  'Facadeskilte og 3D-bogstaver',
  'Wayfinding-systemer',
  'Folie og grafiske løsninger',
  'Ægte glasneon',
  'LED- og lysskilte',
  'Digital signage',
  'Pyloner og fritstående skilte',
  'Prototyper og mockups',
];

const processSteps = [
  {
    number: '01',
    title: 'Briefing',
    description: 'I deler designmateriale og specifikationer. Vi gennemgår sammen og afklarer de tekniske detaljer.',
  },
  {
    number: '02',
    title: 'Tilbud & Rådgivning',
    description: 'Vi udarbejder et detaljeret tilbud med materialevalg, produktionstid og plan for implementering.',
  },
  {
    number: '03',
    title: 'Produktion',
    description: 'Vi producerer med løbende kvalitetskontrol. I opdateres undervejs, så der er fuld gennemsigtighed i processen.',
  },
  {
    number: '04',
    title: 'Levering & Montering',
    description: 'Vi leverer og monterer præcist som aftalt. Dokumentation og garantibevis følger med.',
  },
];

export default function ForDesigners() {
  return (
    <main data-testid="for-designers-page" className="pt-24">
      <SEO 
        title="For Designere & Arkitekter"
        pathname="/for-designere"
        description="Samarbejd med NeonSign som jeres produktionspartner på skiltning og wayfinding. Vi realiserer jeres design – fra idé til færdig installation. Egen produktion i Danmark."
      />
      {/* Hero */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6 animate-fade-in">
                For designere & arkitekter
              </p>
              <h1 
                data-testid="for-designers-title"
                className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-6 animate-fade-in-up"
              >
                Partneren der sikrer, at jeres design fungerer i praksis
              </h1>
              <p className="text-[#666666] text-lg leading-relaxed mb-8 animate-fade-in-up stagger-2">
                I skaber idéen og udtrykket – vi sikrer, at det kan realiseres. Neonsign er jeres produktionspartner på skiltning og wayfinding, hvor design, teknik og udførelse hænger sammen. Vi tager ansvar for, at løsningen bliver produceret korrekt, implementeret rigtigt og leveret som aftalt.
              </p>
              <Link
                to="/kontakt"
                className="btn-dark inline-flex items-center gap-2 animate-fade-in-up stagger-3"
              >
                Start et samarbejde
              </Link>
            </div>
            <div className="image-hover animate-fade-in-up stagger-3">
              <img
                src={imageUrls.large('https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/qni1jgjn_Cabinn%20Copenhagen.png')}
                alt="Wayfinding projekt"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#F8F8F8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
              Hvorfor vælge Neonsign som produktionspartner?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="bg-white p-8">
                <benefit.icon className="w-8 h-8 text-[#1A1A1A] mb-6" />
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#666666] text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="image-hover aspect-square">
                  <img
                    src={imageUrls.medium('https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/47tdp696_251206_NEONSIGN_CHP-68.jpg')}
                    alt="Neonskilt"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="image-hover aspect-square">
                  <img
                    src={imageUrls.medium('https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/urnldo1l_251206_NEONSIGN_CHP-133.jpg')}
                    alt="LED-skilt"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="image-hover aspect-square">
                  <img
                    src={imageUrls.medium('https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/mlqqmv3b_251206_NEONSIGN_CHP-160.jpg')}
                    alt="Facadeskilt"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="image-hover aspect-square">
                  <img
                    src={imageUrls.medium('https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/dfl0xumr_251206_NEONSIGN_CHP-238.jpg')}
                    alt="Tagreklame"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
                Vores kompetencer
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
                Vi realiserer det, I designer – uden at gå på kompromis
              </h2>
              <p className="text-[#666666] leading-relaxed mb-8">
                Med egen produktion og et stærkt netværk af specialister sikrer vi, at selv de mest ambitiøse løsninger kan realiseres. Vi rådgiver om materialer, konstruktion og tekniske muligheder, så designet holder – både visuelt og i praksis.
              </p>
              <ul className="grid grid-cols-2 gap-3">
                {capabilities.map((capability) => (
                  <li key={capability} className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-[#1A1A1A] rounded-full" />
                    <span className="text-[#666666] text-sm">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#F8F8F8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
              Samarbejdsproces
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A]">
              Sådan arbejder vi sammen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step) => (
              <div key={step.number}>
                <span className="font-heading text-5xl font-light text-[#E5E5E5]">
                  {step.number}
                </span>
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mt-4 mb-3">
                  {step.title}
                </h3>
                <p className="text-[#666666] text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Haverkamp Partner Section */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
                Samarbejdspartner
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
                Haverkamp – folieløsninger i verdensklasse
              </h2>
              <p className="text-[#666666] leading-relaxed mb-6">
                Som certificeret partner af Haverkamp tilbyder vi professionelle folieløsninger til vinduer og glas. Haverkamp er en førende tysk producent af højkvalitetsfolier til sikkerhed, solafskærmning, privatliv og dekoration.
              </p>
              <p className="text-[#666666] leading-relaxed mb-8">
                Vi anvender Haverkamps produkter i vores produktion, hvor det er relevant – og sikrer dermed en kvalitet og holdbarhed, der matcher de højeste standarder.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Sikkerhedsfilm og splintsikring',
                  'Solafskærmning og varmekontrol',
                  'Privatlivsfilm og dekoration',
                  'Switchable film (ClickFilm)',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#1A1A1A] rounded-full" />
                    <span className="text-[#666666] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://www.haverkamp.de"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1A1A1A] text-sm font-medium hover:underline inline-flex items-center gap-2"
              >
                Læs mere om Haverkamp
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="image-hover aspect-[4/3] overflow-hidden">
              <img
                src={imageUrls.large('https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/2nofv5q8_Z4HMqXZw.jpg')}
                alt="Haverkamp folieløsninger"
                className="w-full h-[108%] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6">
            Lad os tale om jeres næste projekt
          </h2>
          <p className="text-white/60 mb-10 max-w-lg mx-auto">
            Send os jeres projektmateriale, så vender vi tilbage med et uforpligtende tilbud og rådgivning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              className="btn-light inline-flex items-center gap-2"
            >
              Kontakt os
            </Link>
            <Link
              to="/cases"
              className="btn-dark inline-flex items-center gap-2 !bg-transparent !border !border-white/30 hover:!bg-white/10"
            >
              Se vores arbejde
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
